// Shared visuals for every FIFA chart: palette, axis bounds, Nivo theme, and
// the reference markers (50% coin flip, ceiling). Returns plain objects so this
// stays type-clean under `strict`; the chart components consume them.

/** Dashboard palette from the analysis handoff. */
export const FIFA_PALETTE = {
  primary: "#2b8cbe",
  accent: "#e6550d",
  band: "rgba(43, 140, 190, 0.15)"
};

export const WIN_RATE_AXIS = {min: 0, max: 100, label: "Win rate (%)"};

export const COIN_FLIP = 50;

/** Default chart height (px); matches the ChartCard container. */
export const CHART_HEIGHT = 400;

interface ThemeColors {
  text: string;
  muted: string;
  grid: string;
  tooltipBg: string;
  tooltipText: string;
}

function themeColors(isDark: boolean): ThemeColors {
  return isDark
    ? {
        text: "#e6e6e6",
        muted: "#a0a0a0",
        grid: "rgba(255, 255, 255, 0.12)",
        tooltipBg: "#1f2633",
        tooltipText: "#f5f5f5"
      }
    : {
        text: "#333333",
        muted: "#666666",
        grid: "rgba(0, 0, 0, 0.1)",
        tooltipBg: "#ffffff",
        tooltipText: "#222222"
      };
}

/** Nivo `theme` object for the current color mode. */
export function getNivoTheme(isDark: boolean) {
  const c = themeColors(isDark);
  return {
    background: "transparent",
    textColor: c.text,
    fontSize: 12,
    axis: {
      domain: {line: {stroke: c.grid, strokeWidth: 1}},
      ticks: {
        line: {stroke: c.grid, strokeWidth: 1},
        text: {fill: c.muted, fontSize: 11}
      },
      legend: {text: {fill: c.text, fontSize: 12, fontWeight: 600}}
    },
    grid: {line: {stroke: c.grid, strokeWidth: 1}},
    tooltip: {
      container: {
        background: c.tooltipBg,
        color: c.tooltipText,
        fontSize: 12,
        borderRadius: 6,
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)"
      }
    }
  };
}

/** Dashed 50% coin-flip marker for win-rate charts. */
export function coinFlipMarker(isDark: boolean) {
  return {
    axis: "y",
    value: COIN_FLIP,
    legend: "Coin flip 50%",
    legendPosition: "top-left",
    lineStyle: {
      stroke: isDark ? "#9aa4b2" : "#666666",
      strokeWidth: 1,
      strokeDasharray: "6 4"
    },
    textStyle: {fill: isDark ? "#9aa4b2" : "#666666", fontSize: 11}
  };
}

/** Dotted ceiling marker (max achievable win rate under draws = loss). */
export function ceilingMarker(value: number, isDark: boolean) {
  return {
    axis: "y",
    value,
    legend: `Ceiling ${value}%`,
    legendPosition: "top-right",
    lineStyle: {
      stroke: isDark ? "#c98a5e" : "#b3541e",
      strokeWidth: 1,
      strokeDasharray: "2 3"
    },
    textStyle: {fill: isDark ? "#c98a5e" : "#b3541e", fontSize: 11}
  };
}
